import { useContext } from "react"

import { Icon } from "web/components"
import { formatAsRelativeDate } from "web/services/format"
import { TLOContext } from "web/contexts"

import SetDateIcon from "./SetDateIcon"
import TaskDate from "./TaskDate"
import Assignees from "./Assignees"

import useTaskActions from "./useTaskActions"
import TaskIcon from "./TaskIcon"
import MandatoryIcon from "./MandatoryIcon"

import { isNotAValue, isNonEmptyArray } from "web/services/helpers"

export function requiredFieldsPopulated(deal, task) {
  if (!isNonEmptyArray(task.task_fields)) return true

  const requiredFieldsWithoutAValue = task.task_fields.filter((tf) => {
    return tf.is_required && isNotAValue(deal.values[tf.field_key]?.value)
  })

  return requiredFieldsWithoutAValue.length === 0
}

export function Task({
  id,
  title,
  dueDate,
  completedAt,
  completedBy,
  effectiveDueDate,
  assigneeUsers,
  isStarted,
  isComplete,
  isMandatory,
  showDueDate = true,
  canUpdate,
  canComplete,
  handleShow,
  handleCompleteTask,
  handleUncompleteTask,
  handleSetDueDate,
  handleRemoveDueDate,
}) {
  return (
    <div data-testid={`task-list-task-${id}`} className="task mb-3">
      <div className="group flex items-center truncate">
        <TaskIcon
          isStarted={isStarted}
          isComplete={isComplete}
          canCompleteTask={canComplete}
          onShowTask={handleShow}
          onCompleteTask={handleCompleteTask}
          onUncompleteTask={handleUncompleteTask}
        />
        <button
          data-testid={`button-show-task-${id}`}
          className="mr-2 truncate text-sm"
          onClick={handleShow}
        >
          {title}
        </button>
        {isMandatory && (
          <div className="mr-2">
            <MandatoryIcon />
          </div>
        )}
        {canUpdate && !isComplete && (
          <SetDateIcon handleSetDate={!dueDate ? handleSetDueDate : null} />
        )}
        {showDueDate && !isComplete && (
          <div className="ml-auto">
            <span className="text-sm">
              <TaskDate
                date={effectiveDueDate}
                compact={true}
                readOnly={!canUpdate}
                handleRemoveDate={!!dueDate ? handleRemoveDueDate : null}
              />
            </span>
          </div>
        )}
        {isComplete && (
          <div className="task-completed-date ml-auto flex items-center">
            <Icon name="calendar" outline="true" width="16px" height="16px" />

            <span className="ml-1 text-sm">
              {formatAsRelativeDate({ value: completedAt })}
            </span>
          </div>
        )}
      </div>
      {!isComplete && (
        <div className="max-w-full truncate pl-8 text-xs text-neutral-5">
          <Assignees assigneeUsers={assigneeUsers} />
        </div>
      )}
      {isComplete && (
        <div
          data-testid="task-completed-by"
          className="max-w-full truncate pl-8 text-xs text-neutral-5"
        >
          <span>Completed by </span>
          <span className="task-completer_id">{completedBy}</span>
        </div>
      )}
    </div>
  )
}

export default function TaskContainer({ task, showDueDate = true }) {
  const { data: deal } = useContext(TLOContext)

  const { setTaskDueDate, removeTaskDueDate, completeTask, uncompleteTask } =
    useTaskActions({ dealId: deal.id, task })

  const handleShowTask = () => {
    window.Turbo.visit(
      `/deals/all/view/${task.deal_id}/workflow/tasks/${task.id}`
    )
  }

  const isComplete = task.completer_id
  const isMandatory = !isComplete && task.mandatory

  const canCompleteTask =
    task.can_complete && requiredFieldsPopulated(deal, task)

  return (
    <Task
      isMandatory={isMandatory}
      isComplete={isComplete}
      completedBy={task.completer?.full_name}
      showDueDate={showDueDate}
      dueDate={task.due_date}
      completedAt={task.completed_at}
      effectiveDueDate={task.effective_due_date}
      assigneeUsers={task.assignee_users}
      canUpdate={task.can_update}
      canComplete={canCompleteTask}
      handleShow={handleShowTask}
      handleCompleteTask={completeTask}
      handleUncompleteTask={uncompleteTask}
      handleSetDueDate={setTaskDueDate}
      handleRemoveDueDate={removeTaskDueDate}
      {...task}
    />
  )
}
